import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import USER_TITLES from 'services/enums/users/titles';
import translations from 'services/translations/translations';

import 'style/userManagement/UserManagement.css';

export interface IListedUser {
  email: string;
  firstName: string;
  id: string;
  lastName: string;
  organizationUnit: null | { id: number; name: string };
  phone: null | string;
  // See USER_TITLES
  title: null | string;
}

const emptyFilters = {
  department: '',
  email: '',
  name: '',
  phone: '',
  title: '',
};

// Matches ignoring the case; an empty search matches all
const contains = (value: null | string | undefined, search: string) =>
  search.trim() === '' ||
  (value ?? '')
    .toLocaleLowerCase('fi')
    .includes(search.trim().toLocaleLowerCase('fi'));

/**
 * The users by department: a search for each column at the top, then the departments in
 * the order of their names, each with its users (name, title, phone and email). A row opens
 * the user.
 */
const UsersByDepartment = ({
  onOpen,
  users,
}: {
  onOpen: (user: IListedUser) => void;
  users: IListedUser[];
}): JSX.Element => {
  const { t } = useTranslation([
    translations.admin,
    translations.common,
    translations.userManagement,
  ]);
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.userManagement}:usersByDepartment.${key}`, options);
  const [filters, setFilters] = useState(emptyFilters);

  const nameOf = (user: IListedUser) => `${user.firstName} ${user.lastName}`;
  const titleOf = (user: IListedUser) =>
    user.title ? t(`${translations.userManagement}:titles.${user.title}`) : '';
  const departmentOf = (user: IListedUser) => user.organizationUnit?.name ?? '';

  const departments = Array.from(new Set(users.map(departmentOf))).sort(
    (a, b) =>
      (a === '' ? 1 : 0) - (b === '' ? 1 : 0) || a.localeCompare(b, 'fi')
  );

  const shown = users.filter(
    (user) =>
      contains(nameOf(user), filters.name) &&
      (filters.title === '' || user.title === filters.title) &&
      contains(user.phone, filters.phone) &&
      contains(user.email, filters.email) &&
      (filters.department === '' || departmentOf(user) === filters.department)
  );

  const setFilter = (key: keyof typeof emptyFilters) => (value: string) =>
    setFilters({ ...filters, [key]: value });
  const isFiltered = Object.values(filters).some((value) => value !== '');

  return (
    <div className="users-by-department">
      {/* The search of each column */}
      <div className="users-search">
        <Input
          aria-label={tr('Name')}
          onChange={(event) => setFilter('name')(event.target.value)}
          placeholder={tr('Name')}
          type="search"
          value={filters.name}
        />
        <Input
          aria-label={tr('Title')}
          onChange={(event) => setFilter('title')(event.target.value)}
          type="select"
          value={filters.title}
        >
          <option value="">{tr('All titles')}</option>
          {USER_TITLES.map((title) => (
            <option key={title} value={title}>
              {t(`${translations.userManagement}:titles.${title}`)}
            </option>
          ))}
        </Input>
        <Input
          aria-label={tr('Phone')}
          onChange={(event) => setFilter('phone')(event.target.value)}
          placeholder={tr('Phone')}
          type="search"
          value={filters.phone}
        />
        <Input
          aria-label={tr('Email')}
          onChange={(event) => setFilter('email')(event.target.value)}
          placeholder={tr('Email')}
          type="search"
          value={filters.email}
        />
        <Input
          aria-label={tr('Department')}
          onChange={(event) => setFilter('department')(event.target.value)}
          type="select"
          value={filters.department}
        >
          <option value="">{tr('All departments')}</option>
          {departments.map((department) => (
            <option key={department} value={department}>
              {department || tr('No department')}
            </option>
          ))}
        </Input>
        {isFiltered ? (
          <button
            className="users-search-clear"
            onClick={() => setFilters(emptyFilters)}
            type="button"
          >
            {tr('Clear')}
          </button>
        ) : null}
      </div>
      <p className="users-count">
        {tr('Shown', { count: shown.length, total: users.length })}
      </p>

      {departments.map((department) => {
        const members = shown
          .filter((user) => departmentOf(user) === department)
          .sort((a, b) => nameOf(a).localeCompare(nameOf(b), 'fi'));

        if (members.length === 0) {
          return null;
        }

        return (
          <section className="users-department" key={department || 'none'}>
            <h3>
              {department || tr('No department')}
              <span className="users-department-count">{members.length}</span>
            </h3>
            <table className="users-table">
              <thead>
                <tr>
                  <th>{tr('Name')}</th>
                  <th>{tr('Title')}</th>
                  <th>{tr('Phone')}</th>
                  <th>{tr('Email')}</th>
                </tr>
              </thead>
              <tbody>
                {members.map((user) => (
                  <tr key={user.id} onClick={() => onOpen(user)}>
                    <td className="users-name">{nameOf(user)}</td>
                    <td>{titleOf(user)}</td>
                    <td>
                      {user.phone ? (
                        <a
                          href={`tel:${user.phone.replace(/\s/g, '')}`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {user.phone}
                        </a>
                      ) : null}
                    </td>
                    <td>
                      <a
                        href={`mailto:${user.email}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {user.email}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        );
      })}
      {shown.length === 0 ? <p>{tr('No matches')}</p> : null}
    </div>
  );
};

export default UsersByDepartment;
